/**
 * Platform abstraction for Sanctuary.
 * Detects Electron desktop shell vs plain browser.
 */

export type Platform = 'electron' | 'web';

/** Detect the current runtime platform */
export function detectPlatform(): Platform {
  if (typeof window !== 'undefined' && window.sanctuary?.system?.isElectron === true) {
    return 'electron';
  }
  return 'web';
}

/** Check if running in the Electron desktop shell */
export function isDesktopApp(): boolean {
  return detectPlatform() === 'electron';
}

export interface DesktopMacDetectionInput {
  isElectron: boolean;
  hostPlatform?: string;
  navigatorPlatform?: string;
  userAgent?: string;
}

/** Resolve macOS desktop status without relying on ambient browser globals. */
export function resolveDesktopMac({
  isElectron,
  hostPlatform,
  navigatorPlatform = '',
  userAgent = '',
}: DesktopMacDetectionInput): boolean {
  if (!isElectron) return false;
  if (hostPlatform) return hostPlatform === 'darwin';

  // Compatibility fallback for renderer bundles paired with an older preload.
  return navigatorPlatform.toLowerCase().includes('mac') || userAgent.toLowerCase().includes('mac os');
}

/** Electron on macOS (hiddenInset title bar / traffic lights). */
export function isDesktopMac(): boolean {
  const system = typeof window !== 'undefined' ? window.sanctuary?.system : undefined;
  return resolveDesktopMac({
    isElectron: system?.isElectron === true,
    hostPlatform: system?.platform,
    navigatorPlatform: typeof navigator !== 'undefined' ? navigator.platform : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  });
}

/** Mark the renderer root for native-shell-only layout rules. */
export function applyDesktopPlatformClasses(element: HTMLElement): void {
  const desktopApp = isDesktopApp();
  element.classList.toggle('electron-app', desktopApp);
  element.classList.toggle('electron-mac', desktopApp && isDesktopMac());
}

/** Check if running on a mobile device (responsive web) */
export function isMobile(): boolean {
  if (typeof navigator !== 'undefined') {
    const ua = navigator.userAgent.toLowerCase();
    return /android|iphone|ipad|ipod/.test(ua);
  }
  return false;
}

export { type SanctuaryElectronAPI } from '../types/electron';
